import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useWarehouse } from "../../../apis/warehouse/WarehouseProvider";
import { getUiInterface, getPageName, getWordConnector, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import Warehouse from "../../../types/warehouse";
import ColorPicker from "../../../components/ColorPicker";

const WarehouseCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postWarehouse } = useWarehouse();
    const navigate = useNavigate();
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const descriptionRef = useRef<HTMLInputElement | null>(null);

    const [warehouse, setWarehouse] = useState<Warehouse>({
        id: 0,
        name: "",
        cnpj: "",
        identification: "",
        sz: false,
        color: "",
        colorless: "",
        fontColor: "",
        location: {
            id: 0,
            latitude: "",
            longitude: "",
            address: {
                id: 0,
                street: "",
                number: "",
                complement: "",
                city: "",
                state: "",
                country: "",
                zipCode: ""
            }
        }
    } as Warehouse);

    const navItems: NavBarItem[] = [
        { label: t('warehouse.labels.home'), route: "/", iconName: "house-door" },
        { label: t('warehouse.labels.list'), route: "/Warehouse", iconName: "table" },
    ];

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;

        setWarehouse((prevWarehouse) => {
            const keys = name.split(".");
            const updatedWarehouse = { ...prevWarehouse };
            let current: any = updatedWarehouse;

            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                current[key] = { ...current[key] };
                current = current[key];
            }

            current[keys[keys.length - 1]] = value;
            
            return updatedWarehouse;
        });
    };

    const hadleChangeColor = (newColor: string) => {
        setWarehouse((prevState) => ({
            ...prevState,
            color: newColor.replaceAll("#", ""),
        }));
    };

    const hadleChangeColorless = (newColor: string) => {
        setWarehouse((prevState) => ({
            ...prevState,
            colorless: newColor.replaceAll("#", ""),
        }));
    };

    const hadleChangeFontColor = (newColor: string) => {
        setWarehouse((prevState) => ({
            ...prevState,
            fontColor: newColor.replaceAll("#", ""),
        }));
    };

    const handleChangeSz = (sz: any) => {
        setWarehouse((prevState) => ({
            ...prevState,
            sz: sz.target.checked,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];

        if (!warehouse.name) nullRequiredFields.push(t('warehouse.fields.name'));
        if (!warehouse.location?.latitude) nullRequiredFields.push(t('warehouse.fields.lat'));
        if (!warehouse.location?.longitude) nullRequiredFields.push(t('warehouse.fields.lng'));
        if (!warehouse.location?.address?.street) nullRequiredFields.push(t('warehouse.fields.street'));
        if (!warehouse.location?.address?.number) nullRequiredFields.push(t('warehouse.fields.number'));
        if (!warehouse.location?.address?.complement) nullRequiredFields.push(t('warehouse.fields.complement'));
        if (!warehouse.location?.address?.city) nullRequiredFields.push(t('warehouse.fields.city'));
        if (!warehouse.location?.address?.state) nullRequiredFields.push(t('warehouse.fields.state'));
        if (!warehouse.location?.address?.country) nullRequiredFields.push(t('warehouse.fields.country'));
        // if (!warehouse.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

        if (nullRequiredFields.length > 0) {
            setInvalidFields(new Set(nullRequiredFields));
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        } else {
            setInvalidFields(new Set());
        }

        try {
            const response = await postWarehouse(warehouse);

            if (response?.messages == null && response?.data?.id) {
                const successMessage = getSuccessMessage("createWarehouse", { id: response.data.id });
                navigate(`/Warehouse`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("createWarehouseError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    useEffect(() => {
        if (descriptionRef.current) {
            descriptionRef.current.focus();
        }
    }, []);

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createWarehouse")}</h2>
            <Form 
                onSubmit={handleSubmit}
                style={{paddingBottom: "32px"}}
            >
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('warehouse.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={warehouse.name}
                                isInvalid={invalidFields.has(t('warehouse.fields.name'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="identification">
                            <Form.Label>{t('warehouse.fields.identification')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="identification"
                                value={warehouse.identification}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>{t('warehouse.fields.cnpj')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={warehouse.cnpj}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="color">
                            <Form.Label>{t('warehouse.fields.color')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="color"
                                    value={warehouse.color}
                                    onChange={handleChange}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px" 
                                    }}
                                />
                                <ColorPicker
                                    color={`#${warehouse.color}`}
                                    onChange={(newColor) =>
                                        hadleChangeColor(newColor)
                                    }
                                    blocked={false}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="colorless">
                            <Form.Label>{t('warehouse.fields.colorless')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="colorless"
                                    value={warehouse.colorless}
                                    onChange={handleChange}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px" 
                                    }}
                                />
                                <ColorPicker
                                    color={`#${warehouse.colorless}`}
                                    onChange={(newColor) =>
                                        hadleChangeColorless(newColor)
                                    }
                                    blocked={false}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="fontColor">
                            <Form.Label>{t('warehouse.fields.fontColor')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="fontColor"
                                    value={warehouse.fontColor}
                                    onChange={handleChange}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px" 
                                    }}
                                />
                                <ColorPicker
                                    color={`#${warehouse.fontColor}`}
                                    onChange={(newColor) =>
                                        hadleChangeFontColor(newColor)
                                    }
                                    blocked={false}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4} className="mb-3">
                        <div style={{display: "flex", alignItems: "center"}}>
                            <Form.Group controlId="sz">
                                <Form.Check
                                type="checkbox"
                                label={t('warehouse.fields.sz')}
                                name="sz"
                                checked={warehouse.sz}
                                onChange={handleChangeSz}
                                />
                            </Form.Group>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <h5>{t('warehouse.labels.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('warehouse.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={warehouse.location?.latitude}
                                isInvalid={invalidFields.has(t('warehouse.fields.lat'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('warehouse.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={warehouse.location?.longitude}
                                isInvalid={invalidFields.has(t('warehouse.fields.lng'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('warehouse.labels.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('warehouse.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={warehouse.location?.address?.street}
                                isInvalid={invalidFields.has(t('warehouse.fields.street'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('warehouse.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={warehouse.location?.address?.number}
                                isInvalid={invalidFields.has(t('warehouse.fields.number'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('warehouse.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={warehouse.location?.address?.complement}
                                isInvalid={invalidFields.has(t('warehouse.fields.complement'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('warehouse.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={warehouse.location?.address?.city}
                                isInvalid={invalidFields.has(t('warehouse.fields.city'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('warehouse.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={warehouse.location?.address?.state}
                                isInvalid={invalidFields.has(t('warehouse.fields.state'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('warehouse.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={warehouse.location?.address?.country}
                                isInvalid={invalidFields.has(t('warehouse.fields.country'))}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    {/* <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>Zip Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={warehouse.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col> */}
                </Row>

                <Button variant="primary" onClick={handleSubmit}>
                    {getUiInterface("create")}
                </Button>
            </Form>
        </Container>
    );
};

export default WarehouseCreateView;